import React from 'react';
import {
  IconButton,
  Tooltip,
  Badge,
  CircularProgress,
  useTheme as useMuiTheme,
  Typography,
  Box
} from '@mui/material';
import {
  Sync as SyncIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Event as EventIcon,
  Wifi as WifiIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSyncContext } from '../../context/SyncContext';
import { useAuth } from '../../context/AuthContext';

interface SyncButtonProps {
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'secondary' | 'default' | 'error' | 'info' | 'success' | 'warning';
  showLabel?: boolean;
}

const SyncButton: React.FC<SyncButtonProps> = ({
  size = 'medium',
  color = 'primary',
  showLabel = false
}) => {
  const muiTheme = useMuiTheme();
  const { currentUser } = useAuth();
  const {
    isSyncing,
    lastSyncTime,
    pendingChanges,
    syncSuccess,
    syncError,
    syncWithFirebase,
    isOfflineMode
  } = useSyncContext();

  if (!currentUser) return null;

  // Format the time since last sync
  const formatLastSync = (): string => {
    if (!lastSyncTime) return 'Never synced';

    const now = new Date();
    const diffMs = now.getTime() - lastSyncTime.getTime();
    const diffSec = Math.floor(diffMs / 1000);

    if (diffSec < 60) return 'Just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  };



  const handleSync = () => {
    // If a forced sync is needed, do a forced sync
    // Otherwise do a normal sync
    syncWithFirebase(true);
  };

  // Determine what status to show
  let tooltipTitle = '';
  let buttonColor = color;
  let ButtonIcon = SyncIcon;

  if (isSyncing) {
    tooltipTitle = 'Syncing data with cloud...';
  } else if (syncSuccess) {
    tooltipTitle = 'Sync completed successfully!';
    buttonColor = 'success';
  } else if (syncError) {
    tooltipTitle = `Error: ${syncError}`;
    buttonColor = 'error';
  } else if (isOfflineMode) {
    tooltipTitle = 'Working offline. Changes will sync when you reconnect.';
    ButtonIcon = CloudOffIcon;
    buttonColor = 'warning';
  } else {
    tooltipTitle = `${pendingChanges > 0 ? `${pendingChanges} change${pendingChanges !== 1 ? 's' : ''} pending. ` : ''}Click to sync now`;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title={tooltipTitle}>
        <Badge
          badgeContent={pendingChanges > 0 ? pendingChanges : 0}
          color="error"
          overlap="circular"
          sx={{
            '.MuiBadge-badge': {
              right: -3,
              top: 13,
            },
          }}
        >
          <IconButton
            onClick={handleSync}
            disabled={isSyncing}
            color={buttonColor}
            size={size}
            sx={{ position: 'relative' }}
          >
            {isSyncing ? (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            ) : (
              <ButtonIcon />
            )}
          </IconButton>
        </Badge>
      </Tooltip>

      {showLabel && (
        <Typography
          variant="body2"
          color={buttonColor === 'default' ? 'text.secondary' : buttonColor}
          sx={{
            display: { xs: 'none', sm: 'block' },
            fontSize: size === 'small' ? '0.75rem' : '0.875rem'
          }}
        >
          {isSyncing ? 'Syncing...' :
            isOfflineMode ? 'Offline' :
              'Sync'}
        </Typography>
      )}
    </Box>
  );
};

export default SyncButton; 